import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, CheckCircle, XCircle } from 'lucide-react';
export function ReasoningQualityPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1651">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1652">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1653">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1654">
            <span className="w-8 h-px bg-teal" data-id="element-1655"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1656">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1657"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1658">
            Reasoning Quality
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1659">
            At SyNeural, reasoning quality refers to the ability to evaluate
            claims clearly, weigh evidence appropriately, recognize
            contradictions, distinguish strong arguments from weak ones, and
            process complexity without collapsing into confusion, impulse, or
            oversimplification.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1660">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1661">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1662">
            It is one of the most foundational human capabilities because it
            shapes how people interpret information, form conclusions, and make
            decisions across all areas of life.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1663">
            Reasoning quality is not just a measure of intelligence or
            education. A person may be knowledgeable, verbal, or credentialed
            and still reason poorly under pressure, emotion, ideology, or social
            influence. Strong reasoning involves clarity, structure, evidence
            sensitivity, and the ability to remain grounded when facing
            uncertainty, disagreement, or competing narratives.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1664">
        <div className="max-w-4xl mx-auto" data-id="element-1665">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1666">
            <div className="flex items-center gap-4 mb-6" data-id="element-1667">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1668">
                <Target size={24} data-id="element-1669" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1670">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1671">
              Reasoning quality affects nearly everything else in the SyNeural
              framework. It influences how people judge risk, interpret events,
              respond to persuasion, handle disagreement, and decide what is
              true, useful, or responsible. Weak reasoning can lead to
              confusion, poor judgment, narrative capture, emotional
              overreaction, and avoidable mistakes. Strong reasoning supports
              better decisions, greater independence of thought, and a more
              reliable foundation for growth and responsibility.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Reasoning Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1672">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1673">
          <div className="flex items-center gap-4 mb-6" data-id="element-1674">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1675">
              <CheckCircle size={24} data-id="element-1676" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1677">
              What Strong Reasoning Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1678">
            Strong reasoning involves asking whether a claim is supported,
            whether evidence is strong or weak, whether assumptions are being
            smuggled in, and whether conclusions actually follow from the facts
            presented. It also includes the ability to tolerate complexity
            without reaching for simplistic answers too quickly. People with
            stronger reasoning quality are better able to track nuance,
            recognize when they do not know enough, and update their views when
            better information appears.
          </p>
        </motion.div>
      </section>

      {/* What Weak Reasoning Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1679">
        <div className="max-w-4xl mx-auto" data-id="element-1680">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1681">
            <div className="flex items-center gap-4 mb-6" data-id="element-1682">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1683">
                <XCircle size={24} data-id="element-1684" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1685">
                What Weak Reasoning Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1686">
              Weak reasoning often shows up as contradiction blindness,
              emotional substitution for evidence, simplistic thinking, poor
              causal understanding, and overconfidence in shaky conclusions. It
              may also appear as a tendency to accept arguments because they
              feel satisfying, socially convenient, or identity-reinforcing
              rather than because they are well-supported. In practical terms,
              weak reasoning reduces reliability in learning, civic judgment,
              work, and leadership.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1687">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1688">
          <div className="flex items-center gap-4 mb-6" data-id="element-1689">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1690">
              <Brain size={24} data-id="element-1691" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1692">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1693">
            Reasoning quality can be improved through logic training, argument
            analysis, better reading habits, evidence evaluation, structured
            debate, exposure to complexity, and repeated practice in
            distinguishing signal from noise. It also improves when people learn
            to slow down, question assumptions, and separate emotional reaction
            from analytical judgment. In the SyNeural framework, reasoning
            quality is treated not as a fixed trait, but as a capability that
            can be developed over time.
          </p>
        </motion.div>
      </section>
    </div>;
}